import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Check, Crown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!user) {
      toast.error('Please login to subscribe');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ package: 'monthly' })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const isPremium = user?.subscription_status === 'premium';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      <main className="flex-1 py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-900 mb-4">
              Upgrade to Premium
            </h1>
            <p className="text-lg text-babyblue-600">
              Unlock full access to all German learning content and AI-powered feedback
            </p>
          </div>

          {isPremium && (
            <div className="card p-6 bg-green-50 border-2 border-green-200 mb-8 text-center">
              <Crown className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="font-heading text-xl font-semibold text-green-900 mb-2">
                You're already a Premium member!
              </h3>
              <p className="text-green-700">
                Enjoy unlimited access to all features.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="card p-8 border-2 border-gray-200">
              <h3 className="font-heading text-2xl font-semibold text-babyblue-900 mb-2">Free</h3>
              <p className="text-babyblue-600 mb-6">Basic learning access</p>
              <div className="text-4xl font-bold text-babyblue-900 mb-8">
                $0<span className="text-lg font-normal text-babyblue-600">/month</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700">Access to A1-A2 grammar topics</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700">Basic vocabulary lists</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700">Selected reading exercises</span>
                </li>
                <li className="flex items-start gap-3 opacity-50">
                  <X className="w-5 h-5 text-babyblue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-500">With advertisements</span>
                </li>
              </ul>
            </div>

            <div className="card p-8 border-4 border-primary bg-gradient-to-br from-primary/5 to-primary/10">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-heading text-2xl font-semibold text-babyblue-900">Premium</h3>
                <Crown className="w-6 h-6 text-primary" />
              </div>
              <p className="text-babyblue-600 mb-6">Complete learning experience</p>
              <div className="text-4xl font-bold text-babyblue-900 mb-8">
                $0.99<span className="text-lg font-normal text-babyblue-600">/month</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700 font-medium">All grammar topics (A1-C2)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700 font-medium">Complete vocabulary database</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700 font-medium">All reading &amp; writing exercises</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700 font-medium">AI-powered writing feedback</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700 font-medium">Ad-free experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-babyblue-700 font-medium">Priority support</span>
                </li>
              </ul>
              
              {!isPremium && (
                <button
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="btn-primary w-full text-center"
                >
                  {loading ? 'Processing...' : 'Upgrade Now'}
                </button>
              )}
            </div>
          </div>

          <div className="text-center text-sm text-babyblue-600">
            <p className="mb-2">Secure payment powered by Stripe</p>
            <p>Supports Visa, Mastercard, Debit Cards, PayPal, and more</p>
            <p className="mt-4">Cancel anytime. No hidden fees.</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const X = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default SubscriptionPage;
